import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend,
  Tooltip
} from "chart.js";

import styles from "./financeRevenueComparison.module.css";
import FinanceCardChart from "../financeCardChart/financeCardChart";
import FilterSelect from "../../predictive/filterSelect/filterSelect";
import { financeRevenueSeries } from "../../../services/finance";
import { insightsFilters } from "../../../services/insights";
import { formatDate, formatNumberToCurrency } from "../../../utils/formatter";
import loadingGif from "../../../assets/loading.gif";
import LocaleIcon from "../../../assets/icons/locale.svg";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Legend, Tooltip);

const COLOR_A = "#0962AD";
const COLOR_B = "#2BAD70";

// Comparativo de receita entre dois campus (instituições) ou duas localidades.
// Cada lado busca sua própria série no endpoint leve RevenueSeries — trocar um
// filtro refaz só a chamada daquele lado, sem recarregar a tela.
const FinanceRevenueComparison = ({ institutionId, institutions = [], dateRange, code }) => {
  const hasCampusDimension = institutions.length >= 2;

  const [dimension, setDimension] = useState(hasCampusDimension ? "campus" : "localidade");
  const [optionA, setOptionA] = useState("");
  const [optionB, setOptionB] = useState("");
  const [seriesA, setSeriesA] = useState([]);
  const [seriesB, setSeriesB] = useState([]);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [mode, setMode] = useState("recebido");

  const [localities, setLocalities] = useState([]);
  const [localitiesLoading, setLocalitiesLoading] = useState(false);
  const [localitiesFetched, setLocalitiesFetched] = useState(false);

  const startDate = dateRange?.from ? formatDate(dateRange.from).replaceAll("/", "-") : null;
  const endDate = dateRange?.to ? formatDate(dateRange.to).replaceAll("/", "-") : null;

  const fetchLocalities = async () => {
    if (localities.length > 0 || !institutionId) return;
    try {
      setLocalitiesLoading(true);
      const response = await insightsFilters({
        institutionId,
        predictiveFilter: "filterlocation"
      });
      setLocalities(response.filterValues || []);
      setLocalitiesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar localidades", error);
    } finally {
      setLocalitiesLoading(false);
    }
  };

  // Retorna cleanup que descarta a resposta se a seleção mudar antes dela chegar.
  const startFetchSide = (option, setSeries, setLoading) => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const series = await financeRevenueSeries(
          dimension === "campus"
            ? { institutionId: option, startDate, endDate }
            : { institutionId, startDate, endDate, locality: option }
        );
        if (active) {
          setSeries(series);
        }
      } catch (error) {
        console.log("Erro ao buscar série de receita", error);
        if (active) {
          setSeries([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  };

  useEffect(() => {
    if (!optionA) {
      setSeriesA([]);
      return;
    }
    return startFetchSide(optionA, setSeriesA, setLoadingA);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionA, dimension, institutionId, startDate, endDate]);

  useEffect(() => {
    if (!optionB) {
      setSeriesB([]);
      return;
    }
    return startFetchSide(optionB, setSeriesB, setLoadingB);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionB, dimension, institutionId, startDate, endDate]);

  const handleDimensionChange = (e) => {
    if (!e.target.value) {
      return;
    }
    setDimension(e.target.value);
    setOptionA("");
    setOptionB("");
    setSeriesA([]);
    setSeriesB([]);
  };

  const labelFor = (option) => {
    if (dimension === "campus") {
      const inst = institutions.find((i) => String(i.id) === String(option));
      return inst?.name || option;
    }
    return option;
  };

  const bothSelected = optionA && optionB;
  const isLoading = loadingA || loadingB;
  const isDelinquencyMode = mode === "inadimplencia";

  const labels = (seriesA.length > 0 ? seriesA : seriesB).map((p) => p.month);

  let datasets;
  if (isDelinquencyMode) {
    datasets = [
      {
        label: `Inadimplência — ${labelFor(optionA)}`,
        data: seriesA.map((p) => p.delinquencyRatePct),
        borderColor: COLOR_A,
        backgroundColor: COLOR_A,
        borderWidth: 2
      },
      {
        label: `Inadimplência — ${labelFor(optionB)}`,
        data: seriesB.map((p) => p.delinquencyRatePct),
        borderColor: COLOR_B,
        backgroundColor: COLOR_B,
        borderWidth: 2
      }
    ];
  } else {
    datasets = [
      {
        label: `Recebido — ${labelFor(optionA)}`,
        data: seriesA.map((p) => p.totalReceived),
        borderColor: COLOR_A,
        backgroundColor: COLOR_A,
        borderWidth: 2
      },
      {
        label: `Recebido — ${labelFor(optionB)}`,
        data: seriesB.map((p) => p.totalReceived),
        borderColor: COLOR_B,
        backgroundColor: COLOR_B,
        borderWidth: 2
      }
    ];

    if (mode === "faturado_recebido") {
      datasets.push(
        {
          label: `Faturado — ${labelFor(optionA)}`,
          data: seriesA.map((p) => p.totalBilled),
          borderColor: COLOR_A,
          backgroundColor: COLOR_A,
          borderWidth: 2,
          borderDash: [6, 4]
        },
        {
          label: `Faturado — ${labelFor(optionB)}`,
          data: seriesB.map((p) => p.totalBilled),
          borderColor: COLOR_B,
          backgroundColor: COLOR_B,
          borderWidth: 2,
          borderDash: [6, 4]
        }
      );
    }
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // Tooltip por mês (index) mostra todas as séries comparadas de uma vez,
    // em vez de só o ponto mais próximo do mouse.
    interaction: {
      mode: "index",
      intersect: false
    },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          boxHeight: 6,
          boxWidth: 10,
          color: "#858585",
          font: { size: 14 }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            isDelinquencyMode
              ? `${context.dataset.label}: ${Number(context.raw).toFixed(2)}%`
              : `${context.dataset.label}: ${formatNumberToCurrency(context.raw)}`,
          afterLabel: (context) => {
            if (!isDelinquencyMode) {
              return undefined;
            }
            const series = context.datasetIndex === 0 ? seriesA : seriesB;
            const point = series[context.dataIndex];
            return point ? `Valor vencido: ${formatNumberToCurrency(point.delinquencyValue)}` : undefined;
          }
        }
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        ticks: {
          callback: (value) =>
            isDelinquencyMode ? `${Number(value).toFixed(0)}%` : formatNumberToCurrency(value)
        },
        position: "left",
        title: {
          display: true,
          text: isDelinquencyMode ? "Inadimplência (%)" : "Valores (R$)"
        }
      }
    }
  };

  const hintText = dimension === "campus"
    ? "Selecione dois campus para comparar receita e inadimplência no período filtrado."
    : "Selecione duas localidades para comparar receita e inadimplência no período filtrado.";

  return (
    <FinanceCardChart title="Comparativo de Receita e Inadimplência" code={code} height="auto">
      <div className={styles.controls}>
        {hasCampusDimension && (
          <FilterSelect
            icon={LocaleIcon}
            label="Comparar por"
            value={dimension}
            loading={false}
            options={[
              { id: "campus", name: "Campus" },
              { id: "localidade", name: "Localidade" }
            ]}
            hasFetched={true}
            onChange={handleDimensionChange}
          />
        )}

        <FilterSelect
          icon={LocaleIcon}
          label={dimension === "campus" ? "Campus A" : "Localidade A"}
          value={optionA}
          loading={dimension === "localidade" && localitiesLoading}
          options={dimension === "campus" ? institutions : localities}
          onFocus={dimension === "localidade" ? fetchLocalities : undefined}
          hasFetched={dimension === "campus" ? true : localitiesFetched}
          onChange={(e) => setOptionA(e.target.value)}
        />

        <span className={styles.vs}>vs</span>

        <FilterSelect
          icon={LocaleIcon}
          label={dimension === "campus" ? "Campus B" : "Localidade B"}
          value={optionB}
          loading={dimension === "localidade" && localitiesLoading}
          options={dimension === "campus" ? institutions : localities}
          onFocus={dimension === "localidade" ? fetchLocalities : undefined}
          hasFetched={dimension === "campus" ? true : localitiesFetched}
          onChange={(e) => setOptionB(e.target.value)}
        />

        <div className={styles.modeToggle}>
          <button
            type="button"
            className={`${styles.modeButton} ${mode === "recebido" ? styles.modeButtonActive : ""}`}
            onClick={() => setMode("recebido")}
          >
            Recebido
          </button>
          <button
            type="button"
            className={`${styles.modeButton} ${mode === "faturado_recebido" ? styles.modeButtonActive : ""}`}
            onClick={() => setMode("faturado_recebido")}
          >
            Faturado + Recebido
          </button>
          <button
            type="button"
            className={`${styles.modeButton} ${mode === "inadimplencia" ? styles.modeButtonActive : ""}`}
            onClick={() => setMode("inadimplencia")}
          >
            Inadimplência
          </button>
        </div>
      </div>

      {!bothSelected ? (
        <div className={styles.hint}>
          {hintText}
        </div>
      ) : isLoading ? (
        <div className={styles.loading}>
          <img src={loadingGif} alt="Carregando..." style={{ width: "40px", height: "40px" }} />
        </div>
      ) : (
        <div className={styles.chartArea}>
          <Line data={{ labels, datasets }} options={options} />
        </div>
      )}
    </FinanceCardChart>
  );
};

export default FinanceRevenueComparison;
